import { FC, useMemo } from 'react';
import { ServiceRequestDto } from '@/services/api/contracts/serviceRequests';

export const OffboardUserPayloadViewer: FC<{ request: ServiceRequestDto }> = ({ request }) => {
  const payload = useMemo(() => {
    try {
      return typeof request.payload === 'string' ? JSON.parse(request.payload) : request.payload;
    } catch {
      return request.payload;
    }
  }, [request.payload]);

  if (request.requestType !== 'OffboardUser') {
    return (
      <div className="w-full max-w-3xl mx-auto p-6 bg-white">
        <h2 className="text-2xl font-bold mb-4">Service Request Payload</h2>
        <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto text-gray-800">
          {JSON.stringify(payload, null, 2)}
        </pre>
      </div>
    );
  }

  const {
    fullName,
    personalEmail,
    contactEmail,
    mobileNumber,
    contactMobile,
    targetDate,
    addressLine1,
    addressLine2,
    town,
    region,
    postCode,
    runningTotal,
    hardwareSelections,
    inferredServices
  } = payload || {};

  const email = personalEmail || contactEmail;
  const phone = mobileNumber || contactMobile;

  const address = [addressLine1, addressLine2, town, region, postCode].filter(Boolean).join(', ');

  const formattedTargetDate = (() => {
    if (!targetDate) return 'N/A';
    try {
      const d = new Date(targetDate);
      if (isNaN(d.getTime())) return targetDate;
      return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(d).toUpperCase();
    } catch {
      return targetDate;
    }
  })();

  const formattedTotal = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(
    runningTotal || 0
  );

  const legacyTriggers = Object.keys(payload || {})
    .filter(key => key.startsWith('trigger_') && payload[key] > 0)
    .map(key => ({
      key,
      value: payload[key]
    }));

  const hasHardwareSelections = hardwareSelections && Object.keys(hardwareSelections).length > 0;

  const getSerialNumbersForTriggerType = (triggerName: string) => {
    if (!inferredServices) return [];
    
    // Find all rate cards triggered by this triggerName
    const matchingServices = inferredServices.filter((s: any) => 
      s.rateCard?.propertyBag?.triggerTypes?.includes(triggerName)
    );

    // Collect all serial numbers associated with those rate card IDs
    const sns: string[] = [];
    matchingServices.forEach((s: any) => {
      const rateCardId = s.rateCard?.id;
      if (rateCardId) {
        // Find keys matching `serialNumber_${rateCardId}_`
        Object.keys(payload || {}).forEach(key => {
          if (key.startsWith(`serialNumber_${rateCardId}_`) && payload[key]) {
            sns.push(payload[key]);
          }
        });
      }
    });
    return sns;
  };

  // Find any orphan serial numbers that didn't match a hardware selection
  // This is a safety fallback in case the string matching above misses something
  const allSerialNumbers = Object.keys(payload || {})
    .filter(key => key.startsWith('serialNumber_') && payload[key])
    .map(key => payload[key] as string);
    
  const matchedSerialNumbers = new Set<string>();
  if (hasHardwareSelections) {
    Object.keys(hardwareSelections).forEach(name => {
      getSerialNumbersForTriggerType(name).forEach(sn => matchedSerialNumbers.add(sn));
    });
  }
  
  const orphanSerialNumbers = allSerialNumbers.filter(sn => !matchedSerialNumbers.has(sn));

  return (
    <div className="w-full max-w-3xl mx-auto p-6 sm:p-8 bg-white">
      <h2 className="text-3xl font-bold mb-6 text-gray-900 border-b pb-4">Offboard Request Details</h2>

      <div className="space-y-8">
        <section>
          <h3 className="text-lg font-semibold text-gray-700 mb-3 border-b pb-1">Leaver Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Full Name</p>
              <p className="font-medium text-gray-900">{fullName || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Target Date</p>
              <p className="font-medium text-gray-900">{formattedTargetDate}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium text-gray-900">{email || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Phone</p>
              <p className="font-medium text-gray-900">{phone || 'N/A'}</p>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-gray-700 mb-3 border-b pb-1">Collection Address</h3>
          <p className="text-gray-900">{address || 'N/A'}</p>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-gray-700 mb-3 border-b pb-1">Hardware</h3>
          {hasHardwareSelections ? (
            <ul className="list-disc pl-5 space-y-2 text-gray-900">
              {Object.entries(hardwareSelections).map(([name, quantity]) => {
                const sns = getSerialNumbersForTriggerType(name);
                return (
                  <li key={name}>
                    <div className="font-medium">{String(quantity)}x {name}</div>
                    {sns.length > 0 && (
                      <div className="text-sm text-gray-600 mt-1 pl-2 border-l-2 border-gray-200 ml-1">
                        Serial Numbers: {sns.join(', ')}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : legacyTriggers.length > 0 ? (
            <ul className="list-disc pl-5 space-y-1 text-gray-900">
              {legacyTriggers.map((t, index) => (
                <li key={index}>
                  {t.key}: {String(t.value)} (Legacy Record)
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">No hardware specified.</p>
          )}
        </section>

        {orphanSerialNumbers.length > 0 && (
          <section>
            <h3 className="text-lg font-semibold text-gray-700 mb-3 border-b pb-1">Unmatched Serial Numbers</h3>
            <ul className="list-disc pl-5 space-y-1 text-gray-900 text-sm">
              {orphanSerialNumbers.map((sn, index) => (
                <li key={index}>{sn}</li>
              ))}
            </ul>
          </section>
        )}

        <section className="bg-gray-50 p-4 rounded-lg mt-8 border">
          <div className="flex justify-between items-center font-bold text-xl text-gray-900">
            <span>Estimated Total</span>
            <span>{formattedTotal}</span>
          </div>
        </section>
      </div>
    </div>
  );
};
